"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ONBOARDING_KEY = "mindaloo_onboarded_v1";

const STEPS = [
  {
    id: "welcome",
    label: null,
    heading: "Mindaloo AC",
    body: "Your AI has an identity.\nIt remembers you.\nIt grows with you.",
    cta: "Begin",
  },
  {
    id: "canvas",
    label: "SPATIAL CANVAS",
    heading: "Multiple minds.\nOne workspace.",
    body: "Open agents side by side. Each one focused, each one aware of the others.",
    cta: "Continue",
  },
  {
    id: "command",
    label: "COMMAND BAR",
    heading: "Just type\nwhat you need.",
    body: "Ask anything. Open tools. Switch contexts. Your AI routes it automatically.",
    cta: "Continue",
  },
  {
    id: "ready",
    label: null,
    heading: "You're in.",
    body: "This is your OS now.",
    cta: "Enter Mindaloo",
  },
] as const;

interface MindalooOnboardingProps {
  onComplete: () => void;
  profileName?: string;
}

const STAR_COUNT = 60;

export default function MindalooOnboarding({ onComplete, profileName }: MindalooOnboardingProps) {
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);
  const stars = useRef(
    Array.from({ length: STAR_COUNT }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() > 0.85 ? 2 : 1,
      opacity: Math.random() * 0.5 + 0.1,
    }))
  );

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / STEPS.length) * 100;

  function handleNext() {
    if (isLast) {
      setExiting(true);
      try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch {}
      setTimeout(onComplete, 600);
    } else {
      setStep((s) => s + 1);
    }
  }

  function handleSkip() {
    setExiting(true);
    try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch {}
    setTimeout(onComplete, 300);
  }

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[9999] bg-[#06060a] flex flex-col items-center justify-center"
        >
          {/* Stars */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {stars.current.map((s, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  left: `${s.x}%`,
                  top: `${s.y}%`,
                  width: `${s.size}px`,
                  height: `${s.size}px`,
                  opacity: s.opacity,
                }}
              />
            ))}
            {/* Subtle glow orb */}
            <div
              className="absolute rounded-full"
              style={{
                width: "600px",
                height: "600px",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                background: "radial-gradient(circle, rgba(129,140,248,0.04) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
          </div>

          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-px bg-white/5">
            <motion.div
              className="h-full bg-[#818cf8]/60"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          </div>

          {/* Content */}
          <div className="relative w-full max-w-lg px-8 flex flex-col items-center text-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="flex flex-col items-center"
              >
                {/* Label */}
                {current.label && (
                  <div className="text-[10px] uppercase tracking-[0.35em] text-[#818cf8]/50 mb-6">
                    {current.label}
                  </div>
                )}

                {/* Heading */}
                <h1 className="text-4xl md:text-5xl font-extralight text-white leading-tight mb-6 whitespace-pre-line">
                  {step === 0 && profileName
                    ? `Welcome,\n${profileName}.`
                    : current.heading}
                </h1>

                {/* Body */}
                <p className="text-white/35 text-base font-light leading-relaxed max-w-xs whitespace-pre-line">
                  {current.body}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* CTA */}
            <div className="mt-14 flex flex-col items-center gap-4">
              <motion.button
                onClick={handleNext}
                whileTap={{ scale: 0.97 }}
                className="px-10 py-3 rounded-full text-sm font-medium text-white transition-all"
                style={{
                  background: "rgba(129,140,248,0.15)",
                  border: "1px solid rgba(129,140,248,0.3)",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(129,140,248,0.25)";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(129,140,248,0.15)";
                }}
              >
                {current.cta}
              </motion.button>

              {step < STEPS.length - 1 && (
                <button
                  onClick={handleSkip}
                  className="text-white/15 text-xs hover:text-white/30 transition-colors"
                >
                  Skip
                </button>
              )}
            </div>

            {/* Step dots */}
            <div className="flex gap-2 mt-10">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === step ? "20px" : "4px",
                    height: "4px",
                    backgroundColor: i === step ? "#818cf8" : "rgba(255,255,255,0.12)",
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function useOnboarding() {
  const [shouldOnboard, setShouldOnboard] = useState(false);

  useEffect(() => {
    try {
      const done = localStorage.getItem(ONBOARDING_KEY);
      if (!done) setShouldOnboard(true);
    } catch {}
  }, []);

  function completeOnboarding() {
    setShouldOnboard(false);
    try { localStorage.setItem(ONBOARDING_KEY, "1"); } catch {}
  }

  function resetOnboarding() {
    setShouldOnboard(true);
    try { localStorage.removeItem(ONBOARDING_KEY); } catch {}
  }

  return { shouldOnboard, completeOnboarding, resetOnboarding };
}
